import { useTranslation as usePublicI18n } from "react-i18next";
import { publicText, publicTemplate } from "../publicText";
import React, { useMemo, useState } from "react";
function makeTicks(min, max, count) {
  if (count <= 1 || min === max) return [min];
  const step = (max - min) / (count - 1);
  return Array.from({
    length: count
  }, (_, index) => min + step * index);
}
function safeRange(min, max) {
  if (min !== max) return [min, max];
  const pad = Math.abs(min || 1) * 0.1;
  return [min - pad, max + pad];
}
export function ScientificLineChart({
  labels,
  series,
  title,
  subtitle,
  unit = "",
  yMin,
  yMax,
  yTickCount = 5,
  valueDigits = 1,
  height = 340,
  className = "",
  footer
}) {
  usePublicI18n();
  const [activeIndex, setActiveIndex] = useState(null);
  const chart = useMemo(() => {
    const width = 860;
    const svgHeight = 330;
    const padding = {
      top: 34,
      right: 24,
      bottom: 52,
      left: 62
    };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = svgHeight - padding.top - padding.bottom;
    const allValues = series.flatMap(item => item.values).filter(Number.isFinite);
    const rawMin = yMin ?? Math.min(...allValues);
    const rawMax = yMax ?? Math.max(...allValues);
    const [min, max] = safeRange(rawMin, rawMax);
    const x = index => padding.left + (labels.length <= 1 ? plotWidth / 2 : index / (labels.length - 1) * plotWidth);
    const y = value => padding.top + plotHeight - (value - min) / (max - min) * plotHeight;
    const ticks = makeTicks(min, max, yTickCount);
    return {
      width,
      svgHeight,
      padding,
      plotWidth,
      plotHeight,
      min,
      max,
      x,
      y,
      ticks
    };
  }, [labels, series, yMin, yMax, yTickCount]);
  return <div className={`science-chart ${className}`.trim()} style={{
    minHeight: height
  }}>
      <div className="science-chart__header">
        <div>
          <h3>{publicText(title)}</h3>
          {publicText(subtitle && <p>{publicText(subtitle)}</p>)}
        </div>
        <div className="science-chart__legend">
          {publicText(series.map((item, index) => <span key={item.name}>
              <i className={`series-${index}`} />
              {publicText(item.name)}
            </span>))}
        </div>
      </div>

      <div className="science-chart__viewport">
        <svg viewBox={`0 0 ${chart.width} ${chart.svgHeight}`} role="img" aria-label={publicText(title)} preserveAspectRatio="xMidYMid meet">
          <g className="science-chart__grid">
            {publicText(chart.ticks.map(tick => <g key={tick}>
                <line x1={chart.padding.left} x2={chart.width - chart.padding.right} y1={chart.y(tick)} y2={chart.y(tick)} />
                <text x={chart.padding.left - 12} y={chart.y(tick) + 4} textAnchor="end">
                  {publicText(tick.toFixed(valueDigits))}
                </text>
              </g>))}
          </g>

          <line className="science-chart__axis" x1={chart.padding.left} x2={chart.padding.left} y1={chart.padding.top} y2={chart.svgHeight - chart.padding.bottom} />
          <line className="science-chart__axis" x1={chart.padding.left} x2={chart.width - chart.padding.right} y1={chart.svgHeight - chart.padding.bottom} y2={chart.svgHeight - chart.padding.bottom} />

          {publicText(unit && <text className="science-chart__unit" x={chart.padding.left} y={18}>
              {publicText(unit)}
            </text>)}

          {publicText(labels.map((label, index) => <text className="science-chart__x-label" key={label} x={chart.x(index)} y={chart.svgHeight - 20} textAnchor="middle">
              {publicText(label)}
            </text>))}

          {publicText(series.map((item, seriesIndex) => {
          const points = item.values.map((value, index) => `${chart.x(index)},${chart.y(value)}`).join(" ");
          return <g key={item.name} className={`science-chart__series series-${seriesIndex}`}>
                <polyline points={points} />
                {publicText(item.values.map((value, index) => <g key={`${item.name}-${labels[index]}`}>
                    <circle cx={chart.x(index)} cy={chart.y(value)} r="5">
                      <title>
                        {publicText(labels[index])}{publicText(" / ")}{publicText(item.name)}{publicText(": ")}{publicText(value.toFixed(valueDigits))}
                        {publicText(unit ? ` ${unit}` : "")}
                      </title>
                    </circle>
                  </g>))}
              </g>;
        }))}

          {publicText(activeIndex !== null && <line className="science-chart__focus-line" x1={chart.x(activeIndex)} x2={chart.x(activeIndex)} y1={chart.padding.top} y2={chart.svgHeight - chart.padding.bottom} />)}

          {publicText(labels.map((label, index) => {
          const bandWidth = chart.plotWidth / Math.max(1, labels.length - 1);
          return <rect key={`hit-${label}`} className="science-chart__hit-area" x={chart.x(index) - bandWidth / 2} y={chart.padding.top} width={bandWidth} height={chart.plotHeight} onMouseEnter={() => setActiveIndex(index)} onMouseLeave={() => setActiveIndex(null)} />;
        }))}
        </svg>

        {publicText(activeIndex !== null && <div className="science-chart__tooltip" style={{
        left: `${activeIndex / Math.max(1, labels.length - 1) * 100}%`
      }}>
            <strong>{publicText(labels[activeIndex])}</strong>
            {publicText(series.map((item, index) => <span key={item.name}>
                <i className={`series-${index}`} />
                {publicText(item.name)}{publicText(": ")}{publicText(item.values[activeIndex].toFixed(valueDigits))}
                {publicText(unit ? ` ${unit}` : "")}
              </span>))}
          </div>)}
      </div>

      {publicText(footer && <div className="science-chart__footer">{publicText(footer)}</div>)}
    </div>;
}
export function OpenClosedBars({
  items
}) {
  usePublicI18n();
  return <div className="science-open-bars">
      {publicText(items.map((item, index) => <div className="science-open-bars__item" key={item.label}>
          <div className="science-open-bars__header">
            <div>
              <span>{publicText(item.position)}</span>
              <strong>{publicText(item.label)}</strong>
            </div>
            <b>{publicText(item.openRate.toFixed(1))}{publicText("%")}</b>
          </div>

          <div className="science-open-bars__track" aria-label={publicTemplate(91, [item.label])}>
            <span className={`science-open-bars__fill treatment-${index}`} style={{
          width: `${item.openRate}%`
        }} />
          </div>

          <div className="science-open-bars__meta">
            <span>{publicText("Open ")}{publicText(item.open.toLocaleString("ja-JP"))}</span>
            <span>{publicText("Closed ")}{publicText(item.closed.toLocaleString("ja-JP"))}</span>
            <span>{publicText("Detected ")}{publicText(item.detected.toLocaleString("ja-JP"))}</span>
          </div>
        </div>))}
    </div>;
}
export function PhaseRibbon({
  phases
}) {
  usePublicI18n();
  return <div className="science-phase-ribbon">
      {publicText(phases.map((phase, index) => <div className={`science-phase-ribbon__item phase-${index}`} key={phase.title}>
          <span>{publicText(phase.time)}</span>
          <strong>{publicText(phase.title)}</strong>
          <p>{publicText(phase.description)}</p>
        </div>))}
    </div>;
}
export function DeltaMatrix({
  items
}) {
  usePublicI18n();
  return <div className="science-delta-matrix">
      {publicText(items.map((item, index) => <div className={`science-delta-card treatment-${index}`} key={item.label}>
          <span>{publicText(item.label)}</span>
          <div className="science-delta-card__row">
            <small>{publicText("Open\u7387")}</small>
            <strong className={item.openDelta >= 0 ? "is-positive" : "is-negative"}>
              {publicText(item.openDelta >= 0 ? "+" : "")}
              {publicText(item.openDelta.toFixed(1))}{publicText(" pp")}</strong>
          </div>
          <div className="science-delta-card__row">
            <small>{publicText("\u4E2D\u592E\u5024")}</small>
            <strong className={item.medianDelta >= 0 ? "is-positive" : "is-negative"}>
              {publicText(item.medianDelta >= 0 ? "+" : "")}
              {publicText(item.medianDelta.toFixed(2))}{publicText(" \xB5m")}</strong>
          </div>
          <p>{publicText("\u4F4E\u30B9\u30C8\u30EC\u30B9\u6761\u4EF6\u304B\u3089\u9AD8\u6E29\u30FB\u57F9\u5730\u4E7E\u71E5\u6761\u4EF6\u3078\u306E\u8A18\u8FF0\u7684\u5909\u5316")}</p>
        </div>))}
    </div>;
}
